/**
 * InventoryManagementPage — `/franchise/inventory`.
 *
 * Per-franchise stamp / tracking-number / label-stock allocations. Wraps:
 *
 *   GET  /v1/franchise/inventory/franchise/:id
 *   POST /v1/franchise/inventory/franchise/:id/allocate
 *   POST /v1/franchise/inventory/:allocId/{consume,return,replenish}
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  allocateInventory,
  consumeInventory,
  listFranchises,
  listInventoryForFranchise,
  replenishInventory,
  returnInventory,
  type InventoryItemKind,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const ITEM_KINDS: readonly InventoryItemKind[] = [
  'STAMP',
  'TRACKING_NUMBER_RANGE',
  'LABEL_STOCK',
  'RECEIPT_PAPER',
];

export function InventoryManagementPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Allocate form state
  const [allocKind, setAllocKind] = useState<InventoryItemKind>('STAMP');
  const [allocRef, setAllocRef] = useState('');
  const [allocQty, setAllocQty] = useState('100');
  const [allocMin, setAllocMin] = useState('20');

  const franchises = useQuery({
    queryKey: ['franchise', 'inv', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const allocations = useQuery({
    queryKey: ['franchise', 'inv', 'list', franchiseId],
    queryFn: () => listInventoryForFranchise(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const denied =
    allocations.isError && allocations.error instanceof ApiError &&
    (allocations.error.status === 401 || allocations.error.status === 403);

  const allocate = useMutation({
    mutationFn: () => {
      if (allocRef.length === 0) throw new Error('Item ref required');
      return allocateInventory(franchiseId, {
        itemKind: allocKind,
        itemRef: allocRef,
        qty: Number.parseInt(allocQty, 10),
        minThreshold: Number.parseInt(allocMin, 10),
      });
    },
    onSuccess: () => {
      setAllocRef('');
      void qc.invalidateQueries({ queryKey: ['franchise', 'inv', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const consume = useMutation({
    mutationFn: ({ id, qty }: { readonly id: string; readonly qty: number }) =>
      consumeInventory(id, qty),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'inv', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const ret = useMutation({
    mutationFn: ({ id, qty, reason }: { readonly id: string; readonly qty: number; readonly reason: string }) =>
      returnInventory(id, qty, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'inv', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const repl = useMutation({
    mutationFn: ({ id, qty }: { readonly id: string; readonly qty: number }) =>
      replenishInventory(id, qty),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'inv', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory management"
        description="Stamp / tracking-number / label / receipt-paper allocations per franchise."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Franchise</span>
        {franchises.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <select
            value={franchiseId}
            onChange={(e) => setFranchiseId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm flex-1 min-w-[260px]"
          >
            <option value="">Select…</option>
            {franchises.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.legalName} ({f.franchiseNumber})
              </option>
            ))}
          </select>
        )}
      </div>

      {franchiseId.length > 0 ? (
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">Allocate stock</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Kind</label>
              <select
                value={allocKind}
                onChange={(e) => setAllocKind(e.target.value as InventoryItemKind)}
                className="px-3 py-2 border border-border rounded-lg text-sm"
              >
                {ITEM_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Item ref</label>
              <input
                value={allocRef}
                onChange={(e) => setAllocRef(e.target.value)}
                placeholder="e.g. NGN500-stamp-batch-2026-04"
                className="px-3 py-2 border border-border rounded-lg text-sm min-w-[260px]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Qty</label>
              <input
                type="number"
                value={allocQty}
                onChange={(e) => setAllocQty(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm w-28"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Min</label>
              <input
                type="number"
                value={allocMin}
                onChange={(e) => setAllocMin(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm w-20"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setActionErr(null);
                allocate.mutate();
              }}
              disabled={allocate.isPending}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              {allocate.isPending ? 'Allocating…' : 'Allocate'}
            </button>
          </div>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise.
        </p>
      ) : allocations.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseInventory/read</code> required.
        </DeferredNotice>
      ) : allocations.isError ? (
        <DeferredNotice title="Service unreachable">
          {(allocations.error as Error).message}
        </DeferredNotice>
      ) : (allocations.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No allocations yet.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Ref</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Issued</th>
                <th className="text-right px-4 py-2">Consumed</th>
                <th className="text-right px-4 py-2">Returned</th>
                <th className="text-right px-4 py-2">Min</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {allocations.data?.items.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs text-gray-700">{a.itemKind}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-gray-700">{a.itemRef}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-2 text-right text-xs">{a.issuedQty}</td>
                  <td className="px-4 py-2 text-right text-xs">{a.consumedQty}</td>
                  <td className="px-4 py-2 text-right text-xs">{a.returnedQty}</td>
                  <td className="px-4 py-2 text-right text-xs">{a.minThreshold}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const qty = Number.parseInt(window.prompt('Consume qty?') ?? '0', 10);
                        if (!Number.isFinite(qty) || qty <= 0) return;
                        setActionErr(null);
                        consume.mutate({ id: a.id, qty });
                      }}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                    >
                      Consume
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const qty = Number.parseInt(window.prompt('Return qty?') ?? '0', 10);
                        if (!Number.isFinite(qty) || qty <= 0) return;
                        const reason = window.prompt('Reason?') ?? '';
                        if (reason.length === 0) return;
                        setActionErr(null);
                        ret.mutate({ id: a.id, qty, reason });
                      }}
                      className="text-xs font-semibold text-amber-700 hover:underline"
                    >
                      Return
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const qty = Number.parseInt(window.prompt('Replenish qty?') ?? '0', 10);
                        if (!Number.isFinite(qty) || qty <= 0) return;
                        setActionErr(null);
                        repl.mutate({ id: a.id, qty });
                      }}
                      className="text-xs font-semibold text-blue-700 hover:underline"
                    >
                      Replenish
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
